import { toast } from '@/shared/lib/toast';
import { wsLogger } from '@/shared/lib/logger';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import {
  payloadString,
  payloadNumber,
  payloadObject,
  pStr,
  pNum,
} from '../realtime-sync.payload';
import {
  setProgressAnchor,
  getProgressAnchor,
  removeProgressAnchor,
} from '@/shared/lib/progress';
import type { DataChangedEvent } from '../realtime-sync.types';
import { getAppRouter } from '@/shared/lib/app-router';
import { useJobQueueStore } from '@/features/design/job-queue';
import { upsertRender } from '@/features/design/render/model/renders-cache';
import type { Render } from '@/types/render';

/**
 * Handle render created - add pending job for upscale and regular renders.
 * This ensures the UI shows progress immediately, before queue snapshot arrives.
 *
 * For upscale renders: uses addUpscaleJob (existing pattern).
 * For regular renders: uses addPendingRenderJob (safety net for multi-tab scenarios).
 */
export async function handleRenderCreated(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  if (!payload || !event.id) return;

  const renderType = payloadString(payload, 'render_type');
  const store = useJobQueueStore.getState();

  // Check if job already exists
  const existingJob = store.jobQueue.find((j) => j.id === event.id);
  if (existingJob) {
    wsLogger.debug('[RealtimeSync] Job already in queue:', event.id);
    return;
  }

  const now = new Date().toISOString();

  if (renderType === 'upscale') {
    // Create pending upscale job
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const upscaleJob: any = {
      id: event.id,
      jobType: 'upscale' as const,
      scenarioType: 'upscale' as const,
      status: 'pending',
      startedAt: null,
      durationPredicted: 30, // Default upscale duration
      attemptNumber: 1,
      createdAt: now,
      updatedAt: now,
      completedAt: null,
      conceptId: pStr(payload, 'concept_id', ''),
      projectId: '', // Not provided in event
      conceptName: null,
      conceptSequenceNumber: 0,
      projectName: '',
      conceptAngleId: pStr(payload, 'concept_angle_id', ''),
      angleType: 'derivative' as const, // Safe default
      originalRenderId: pStr(payload, 'original_render_id', ''),
      imageUrl: null,
      angleUrl: null,
    };

    store.addUpscaleJob(upscaleJob);

    wsLogger.info('[RealtimeSync] Upscale job created:', {
      jobId: event.id,
      originalRenderId: payload.original_render_id,
      conceptAngleId: payload.concept_angle_id,
    });
  } else {
    // Regular render — add pending job (safety net for multi-tab scenarios)
    const renderJob = {
      id: event.id,
      jobType: 'render' as const,
      status: 'pending' as const,
      startedAt: null,
      durationPredicted: pNum(payload, 'duration_predicted', 60),
      attemptNumber: 1,
      createdAt: now,
      updatedAt: now,
      completedAt: null,
      conceptId: pStr(payload, 'concept_id', ''),
      projectId: pStr(payload, 'project_id', ''),
      conceptName: null,
      conceptSequenceNumber: 0,
      projectName: '',
      conceptAngleId: pStr(payload, 'concept_angle_id', ''),
      angleType: pStr(payload, 'angle_type', 'main') as 'main' | 'derivative',
      scenarioType: renderType,
      imageUrl: null,
      angleUrl: null,
    };

    store.addPendingRenderJob(renderJob);

    wsLogger.info('[RealtimeSync] Render job created from WebSocket:', {
      jobId: event.id,
      conceptAngleId: payload.concept_angle_id,
    });
  }
}

/**
 * Handle render started - update job to processing status with timing info.
 * Handles both regular renders and upscale renders.
 */
export async function handleRenderStarted(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  if (!payload || !event.id) return;

  const renderType = payloadString(payload, 'render_type');

  const store = useJobQueueStore.getState();
  const startedAt = pStr(payload, 'started_at', new Date().toISOString());
  const durationPredicted =
    payloadNumber(payload, 'duration_predicted') ||
    (renderType === 'upscale' ? 30 : 60);

  if (renderType === 'upscale') {
    store.updateUpscaleJobStarted(event.id, startedAt, durationPredicted);
  } else {
    // Update existing pending job to processing
    store.updateJobStarted(event.id, startedAt, durationPredicted);
  }

  // Create monotonic progress anchor
  if (durationPredicted > 0) {
    setProgressAnchor(event.id, {
      anchorUptime: performance.now(),
      anchorProgress: 0,
      durationPredicted,
    });
  }

  wsLogger.info('[RealtimeSync] Render started:', {
    jobId: event.id,
    renderType,
    startedAt,
    durationPredicted,
  });

  // Invalidate concept list so other devices see the new concept appear
  // (a started render means a freshly created concept). API ships project_id
  // in started payloads since 9f21d6822.
  const projectIdForList = payloadString(payload, 'project_id');
  if (projectIdForList) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.list(projectIdForList),
    });
  }
}

export async function handleRenderCompleted(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  if (!payload || !event.id) return;

  const renderType = payloadString(payload, 'render_type');

  // Skip intermediate renders - only filter out explicit intermediate type
  if (renderType === 'intermediate_photorealistic') {
    wsLogger.debug('[RealtimeSync] Skipping intermediate render:', {
      renderId: event.id,
      renderType,
    });
    return;
  }

  const store = useJobQueueStore.getState();

  // For upscale completions, remove job from protection cache
  // event.id is original_render_id, but job was tracked by upscale_render_id
  if (renderType === 'upscale') {
    const upscaleRenderId = payloadString(payload, 'upscale_render_id');
    // Use upscale_render_id if available (the actual job ID), otherwise search by originalRenderId
    if (upscaleRenderId) {
      store.removeUpscaleJobById(upscaleRenderId);
    } else {
      store.removeUpscaleFromProtectionCache(event.id);
    }
    wsLogger.info('[RealtimeSync] Removed upscale job from protection cache:', {
      originalRenderId: event.id,
      upscaleRenderId,
    });
  }

  // Build render-like object from payload. Fields not in the WS payload
  // (priority, settings, timestamps for create) get filled by the next
  // concept-detail refetch — render-list consumers tolerate the partial
  // shape the same way the old store-side Map did.
  const conceptIdForCache = payloadString(payload, 'concept_id');
  const renderData = {
    id: event.id,
    type: 'render',
    attributes: {
      status: payload.status,
      completed_at: payload.completed_at,
      file_url: payload.file_url || payload.image_url,
      angle_type: payload.angle_type,
      render_type: payload.render_type,
      is_upscaled: payload.is_upscaled,
      settings: payload.settings,
      setup_hash: payload.setup_hash,
      llm_feedback: payloadObject(payload, 'llm_feedback'),
    },
    relationships: {
      concept_angle: payloadString(payload, 'concept_angle_id')
        ? {
            data: {
              id: payloadString(payload, 'concept_angle_id')!,
              type: 'concept_angle',
            },
            meta: conceptIdForCache
              ? { concept_id: conceptIdForCache }
              : undefined,
          }
        : undefined,
      file: payloadString(payload, 'file_url')
        ? { meta: { url: payloadString(payload, 'file_url')! } }
        : undefined,
    },
  } as unknown as Render;

  // Server-state lives in TanStack Query (STATE_POLICY): list per concept +
  // detail per renderId. Job-queue store keeps only UI job state.
  upsertRender(renderData, conceptIdForCache);

  // Clean up progress anchor for completed render
  if (event.id) {
    removeProgressAnchor(event.id);
  }

  // Clean up superseded (failed/cancelled) jobs for this angle
  const conceptAngleId = payloadString(payload, 'concept_angle_id');
  if (conceptAngleId) {
    store.cleanupSupersededJobs(conceptAngleId, event.id!);
  }

  wsLogger.info('[RealtimeSync] Render completed:', {
    renderId: event.id,
    angleType: payload.angle_type,
  });

  // Show success toast — clicking the body navigates to the concept (no
  // inline "View" button). Mirrors native iOS/macOS notification banners and
  // keeps parity with Apple ToastManager + Android GlobalSnackbarManager.
  const projectIdForNav = payloadString(payload, 'project_id');
  const conceptIdForNav = payloadString(payload, 'concept_id');
  const angleType = payloadString(payload, 'angle_type');

  // API-driven messages: server owns user-facing copy. Skip toast when the
  // payload doesn't carry localized text instead of shipping English fallbacks.
  {
    const title = payloadString(payload, 'title');
    if (title) {
      const description = payloadString(payload, 'message');
      const canNavigate = Boolean(projectIdForNav && conceptIdForNav);

      toast.success(title, {
        ...(description && { description }),
        duration: 40000,
        ...(canNavigate && {
          style: { cursor: 'pointer' },
          onClick: () => {
            const angleParam =
              angleType === 'derivative' && conceptAngleId
                ? `&angleId=${conceptAngleId}`
                : '';
            // Arrival token (`n`) = completed render id, so every tap is a
            // distinct navigation. When the user is already on this concept the
            // page doesn't remount; the changing token is what makes the editor
            // re-apply the Design tab, select the right angle, and scroll the
            // image back into view. Mirrors Apple arrivalNonce.
            const nonceParam = event.id ? `&n=${event.id}` : '';
            getAppRouter()?.push(
              `/project/${projectIdForNav}/concept/${conceptIdForNav}?tab=design${angleParam}${nonceParam}`
            );
          },
        }),
      });
    }
  }

  // Invalidate job history
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });

  // Invalidate concept list so project page carousel updates thumbnails
  const projectIdForRender = payloadString(payload, 'project_id');
  if (projectIdForRender) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.list(projectIdForRender),
    });
  }
}

export async function handleRenderProgress(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;
  if (!payload) return;

  const status = payloadString(payload, 'status');
  const message = payloadString(payload, 'message');

  if (status === 'quality_rerender' && message) {
    wsLogger.info('[RealtimeSync] Render quality rerender:', {
      renderId: event.id,
      status,
    });

    toast.info(message, {
      duration: 6000,
    });
  }
}

export async function handleRenderFailed(event: DataChangedEvent): Promise<void> {
  const payload = event.payload;

  wsLogger.warn('[RealtimeSync] Render failed:', {
    renderId: event.id,
    error: payload?.error,
    code: payload?.code,
  });

  // Update job status to failed in store (so UI shows failed overlay)
  if (event.id) {
    const store = useJobQueueStore.getState();
    store.updateJobStatus(event.id, 'failed');
    removeProgressAnchor(event.id);
  }

  // Show error toast — API-driven only (skip if no localized title in payload).
  {
    const title = payloadString(payload, 'title');
    if (title) {
      const description =
        payloadString(payload, 'message') ?? payloadString(payload, 'error');
      toast.error(title, {
        ...(description && { description }),
        duration: 8000,
      });
    }
  }

  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });

  // Invalidate concept list so failed/cancelled state is reflected in carousel.
  // API ships project_id in failed/cancelled payloads since 9f21d6822.
  const projectIdForList = payloadString(payload, 'project_id');
  if (projectIdForList) {
    queryClient.invalidateQueries({
      queryKey: cacheKeys.concepts.list(projectIdForList),
    });
  }
}

/**
 * Handle render transitioned - replace old render ID with new one.
 * This happens when intermediate_photorealistic render completes and
 * OutputStyleTransferJob creates a new final_styled render.
 */
export async function handleRenderTransitioned(
  event: DataChangedEvent
): Promise<void> {
  const payload = event.payload;
  if (!payload) return;

  const sourceRenderId = payloadString(payload, 'source_render_id');
  const newRenderId = payloadString(payload, 'new_render_id');
  if (!sourceRenderId || !newRenderId) return;

  const store = useJobQueueStore.getState();
  const startedAt = pStr(payload, 'started_at', new Date().toISOString());
  const durationPredicted = pNum(payload, 'duration_predicted', 30);

  // Replace old job ID with new one in the store
  store.transitionJob(
    sourceRenderId,
    newRenderId,
    startedAt,
    durationPredicted
  );

  // Move progress anchor from old ID to new ID
  // Preserve current progress to avoid visual reset during style transfer
  const oldAnchor = getProgressAnchor(sourceRenderId);
  removeProgressAnchor(sourceRenderId);
  if (durationPredicted > 0) {
    let currentProgress = 0;
    if (oldAnchor && oldAnchor.durationPredicted > 0) {
      const elapsedMs = performance.now() - oldAnchor.anchorUptime;
      const elapsedSec = elapsedMs / 1000;
      currentProgress = Math.min(
        oldAnchor.anchorProgress +
          (elapsedSec / oldAnchor.durationPredicted) * 100,
        95
      );
    }
    setProgressAnchor(newRenderId, {
      anchorUptime: performance.now(),
      anchorProgress: currentProgress,
      durationPredicted,
    });
  }

  wsLogger.info('[RealtimeSync] Render transitioned:', {
    sourceRenderId,
    newRenderId,
    durationPredicted,
  });

  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}
